import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
    CfActivityActionRequest,
    CfApplyActivityTemplateRequest,
    CfCloseReadiness,
    CfCloseRequest,
    CfCloseResult,
    CfCostSheet,
    CfExecutionActivity,
    CfExecutionFile,
    CfExecutionWorkspace,
    CfJobCostElement,
    CfRequestAction,
    CfRequestSummary,
    CfShippingPaymentRequest,
    CfSupplierBookingRequest,
    LogisticsProject,
    LogisticsProjectSummary,
    LogisticsProgram,
    LogisticsProgramSummary,
    ProjectEvent,
    StatusChangeRequest,
    ProjectResourceLink,
    LinkableProjectResource,
    ProjectFinancialFact,
    ProjectFinancialSummary,
    ProjectReportWorkspace,
    ProjectCompletionReportGenerateRequest,
    ProjectCompletionReportExport,
    ProjectPlanAssignment,
    ProjectPlanBaseline,
    ProjectPlanBaselineRequest,
    ProjectPlanBulkTaskUpdate,
    ProjectPlanDependency,
    ProjectPlanTask,
    ProjectPlanWorkspace,
    ProjectRequirementAllocation,
    ProjectCalendarEvent,
    ProjectRequirementFulfillmentLink,
    ProjectRequirementGenerationResponse,
    ProjectRequirementLine,
    ProjectRequirementsSummary,
    ProjectMilestone,
    ProjectComment,
    ProjectCustomerView,
    ProjectDocument,
    ProjectDocumentRequirement,
    ProjectChangeRequest,
    ProjectClosureReview,
    ProjectParticipant,
    ProjectRiskIssue,
    ProjectTemplate,
    ProjectWorkPackage
} from './models/logistics-project.model';

export interface LogisticsProjectPage {
    content: LogisticsProjectSummary[];
    totalElements: number;
}

@Injectable({
    providedIn: 'root'
})
export class LogisticsProjectsService {
    private http = inject(HttpClient);
    private readonly projectsApiUrl = `${environment.restApiUrl}logistics-projects`;
    private readonly cfRequestsApiUrl = `${environment.restApiUrl}cf-execution-requests`;
    private readonly programsApiUrl = `${environment.restApiUrl}logistics-programs`;

    // ── Projects ──────────────────────────────────────────────

    getAllProjects(owningCompanyId: number): Observable<LogisticsProjectSummary[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<LogisticsProjectSummary[]>(this.projectsApiUrl, { params });
    }

    lookupProjects(owningCompanyId: number, search = '', selectedIds: number[] = []): Observable<LogisticsProjectSummary[]> {
        let params = new HttpParams().set('owningCompanyId', owningCompanyId);
        if (search.trim()) params = params.set('search', search.trim());
        selectedIds.forEach(id => params = params.append('selectedIds', id));
        return this.http.get<LogisticsProjectSummary[]>(`${this.projectsApiUrl}/lookup`, { params });
    }

    getProjectsPage(owningCompanyId: number, page: number, size: number, search: string,
                    status: string | null, projectType: string | null, sortBy: string,
                    sortDirection: 'asc' | 'desc'): Observable<LogisticsProjectPage> {
        let params = new HttpParams()
            .set('owningCompanyId', owningCompanyId)
            .set('page', page)
            .set('size', size)
            .set('sortBy', sortBy)
            .set('sortDirection', sortDirection);
        if (search.trim()) params = params.set('search', search.trim());
        if (status) params = params.set('status', status);
        if (projectType) params = params.set('projectType', projectType);
        return this.http.get<LogisticsProjectPage>(`${this.projectsApiUrl}/page`, { params });
    }

    getProject(id: number, owningCompanyId: number): Observable<LogisticsProject> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<LogisticsProject>(`${this.projectsApiUrl}/${id}`, { params });
    }

    createProject(owningCompanyId: number, project: LogisticsProject): Observable<LogisticsProject> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<LogisticsProject>(this.projectsApiUrl, project, { params });
    }

    createProjectFromTemplate(templateCode: string, owningCompanyId: number, project: LogisticsProject): Observable<LogisticsProject> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<LogisticsProject>(`${this.projectsApiUrl}/from-template/${templateCode}`, project, { params });
    }

    createProjectFromQuotation(quotationId: number, owningCompanyId: number): Observable<LogisticsProject> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<LogisticsProject>(`${this.projectsApiUrl}/from-quotation/${quotationId}`, {}, { params });
    }

    createProjectFromContract(contractId: number, owningCompanyId: number): Observable<LogisticsProject> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<LogisticsProject>(`${this.projectsApiUrl}/from-contract/${contractId}`, {}, { params });
    }

    updateProject(id: number, owningCompanyId: number, project: LogisticsProject): Observable<LogisticsProject> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.put<LogisticsProject>(`${this.projectsApiUrl}/${id}`, project, { params });
    }

    deleteProject(id: number, owningCompanyId: number): Observable<void> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.delete<void>(`${this.projectsApiUrl}/${id}`, { params });
    }

    changeStatus(id: number, owningCompanyId: number, request: StatusChangeRequest): Observable<LogisticsProject> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<LogisticsProject>(`${this.projectsApiUrl}/${id}/status`, request, { params });
    }

    closeProject(id: number, owningCompanyId: number, request?: StatusChangeRequest): Observable<LogisticsProject> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<LogisticsProject>(`${this.projectsApiUrl}/${id}/close`, request || {}, { params });
    }

    reopenProject(id: number, owningCompanyId: number, request?: StatusChangeRequest): Observable<LogisticsProject> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<LogisticsProject>(`${this.projectsApiUrl}/${id}/reopen`, request || {}, { params });
    }

    getCfExecutionWorkspace(projectId: number, owningCompanyId: number): Observable<CfExecutionWorkspace> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<CfExecutionWorkspace>(`${this.projectsApiUrl}/${projectId}/cf-execution`, { params });
    }

    saveCfExecutionProfile(projectId: number, owningCompanyId: number, profile: CfExecutionFile): Observable<CfExecutionFile> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.put<CfExecutionFile>(`${this.projectsApiUrl}/${projectId}/cf-execution`, profile, { params });
    }

    applyCfActivityTemplate(projectId: number, owningCompanyId: number, request: CfApplyActivityTemplateRequest = {}): Observable<CfExecutionActivity[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<CfExecutionActivity[]>(
            `${this.projectsApiUrl}/${projectId}/cf-execution/activities/apply-template`,
            request,
            { params });
    }

    completeCfActivity(projectId: number, owningCompanyId: number, activityId: number,
                       request: CfActivityActionRequest = {}): Observable<CfExecutionActivity> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<CfExecutionActivity>(
            `${this.projectsApiUrl}/${projectId}/cf-execution/activities/${activityId}/complete`,
            request,
            { params });
    }

    reopenCfActivity(projectId: number, owningCompanyId: number, activityId: number,
                     request: CfActivityActionRequest = {}): Observable<CfExecutionActivity> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<CfExecutionActivity>(
            `${this.projectsApiUrl}/${projectId}/cf-execution/activities/${activityId}/reopen`,
            request,
            { params });
    }

    getCfCostSheet(projectId: number, owningCompanyId: number): Observable<CfCostSheet> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<CfCostSheet>(`${this.projectsApiUrl}/${projectId}/cf-execution/cost-sheet`, { params });
    }

    applyCfCostDefaults(projectId: number, owningCompanyId: number): Observable<CfCostSheet> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<CfCostSheet>(
            `${this.projectsApiUrl}/${projectId}/cf-execution/cost-sheet/apply-defaults`,
            {},
            { params });
    }

    saveCfCostLine(projectId: number, owningCompanyId: number, line: CfJobCostElement): Observable<CfJobCostElement> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        const url = line.costLineId
            ? `${this.projectsApiUrl}/${projectId}/cf-execution/cost-sheet/lines/${line.costLineId}`
            : `${this.projectsApiUrl}/${projectId}/cf-execution/cost-sheet/lines`;
        return line.costLineId
            ? this.http.put<CfJobCostElement>(url, line, { params })
            : this.http.post<CfJobCostElement>(url, line, { params });
    }

    deleteCfCostLine(projectId: number, owningCompanyId: number, costLineId: number): Observable<void> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.delete<void>(
            `${this.projectsApiUrl}/${projectId}/cf-execution/cost-sheet/lines/${costLineId}`,
            { params });
    }

    // ── Programs ──────────────────────────────────────────────

    getCfRequests(projectId: number, owningCompanyId: number): Observable<CfRequestSummary> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<CfRequestSummary>(`${this.projectsApiUrl}/${projectId}/cf-execution/requests`, { params });
    }

    getCfCloseReadiness(projectId: number, owningCompanyId: number): Observable<CfCloseReadiness> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<CfCloseReadiness>(`${this.projectsApiUrl}/${projectId}/cf-execution/close-readiness`, { params });
    }

    closeCfExecution(projectId: number, owningCompanyId: number, request: CfCloseRequest = {}): Observable<CfCloseResult> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<CfCloseResult>(`${this.projectsApiUrl}/${projectId}/cf-execution/close`, request, { params });
    }

    reopenCfExecution(projectId: number, owningCompanyId: number, request: CfCloseRequest = {}): Observable<CfCloseResult> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<CfCloseResult>(`${this.projectsApiUrl}/${projectId}/cf-execution/reopen`, request, { params });
    }

    saveCfSupplierBooking(projectId: number, owningCompanyId: number,
                          request: CfSupplierBookingRequest): Observable<CfSupplierBookingRequest> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        const url = request.requestId
            ? `${this.projectsApiUrl}/${projectId}/cf-execution/supplier-bookings/${request.requestId}`
            : `${this.projectsApiUrl}/${projectId}/cf-execution/supplier-bookings`;
        return request.requestId
            ? this.http.put<CfSupplierBookingRequest>(url, request, { params })
            : this.http.post<CfSupplierBookingRequest>(url, request, { params });
    }

    supplierBookingAction(projectId: number, owningCompanyId: number, requestId: number,
                          actionName: string, request: CfRequestAction = {}): Observable<CfSupplierBookingRequest> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<CfSupplierBookingRequest>(
            `${this.projectsApiUrl}/${projectId}/cf-execution/supplier-bookings/${requestId}/${actionName}`,
            request,
            { params });
    }

    saveCfShippingPayment(projectId: number, owningCompanyId: number,
                          request: CfShippingPaymentRequest): Observable<CfShippingPaymentRequest> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        const url = request.paymentRequestId
            ? `${this.projectsApiUrl}/${projectId}/cf-execution/shipping-payments/${request.paymentRequestId}`
            : `${this.projectsApiUrl}/${projectId}/cf-execution/shipping-payments`;
        return request.paymentRequestId
            ? this.http.put<CfShippingPaymentRequest>(url, request, { params })
            : this.http.post<CfShippingPaymentRequest>(url, request, { params });
    }

    shippingPaymentAction(projectId: number, owningCompanyId: number, requestId: number,
                          actionName: string, request: CfRequestAction = {}): Observable<CfShippingPaymentRequest> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<CfShippingPaymentRequest>(
            `${this.projectsApiUrl}/${projectId}/cf-execution/shipping-payments/${requestId}/${actionName}`,
            request,
            { params });
    }

    getCfSupplierBookingWorkbench(owningCompanyId: number, status?: string): Observable<CfSupplierBookingRequest[]> {
        let params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        if (status) params = params.set('status', status);
        return this.http.get<CfSupplierBookingRequest[]>(`${this.cfRequestsApiUrl}/supplier-bookings`, { params });
    }

    getCfShippingPaymentWorkbench(owningCompanyId: number, status?: string): Observable<CfShippingPaymentRequest[]> {
        let params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        if (status) params = params.set('status', status);
        return this.http.get<CfShippingPaymentRequest[]>(`${this.cfRequestsApiUrl}/shipping-payments`, { params });
    }

    getAllPrograms(owningCompanyId: number): Observable<LogisticsProgramSummary[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<LogisticsProgramSummary[]>(this.programsApiUrl, { params });
    }

    getProgram(id: number, owningCompanyId: number): Observable<LogisticsProgram> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<LogisticsProgram>(`${this.programsApiUrl}/${id}`, { params });
    }

    createProgram(owningCompanyId: number, program: LogisticsProgram): Observable<LogisticsProgram> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<LogisticsProgram>(this.programsApiUrl, program, { params });
    }

    updateProgram(id: number, owningCompanyId: number, program: LogisticsProgram): Observable<LogisticsProgram> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.put<LogisticsProgram>(`${this.programsApiUrl}/${id}`, program, { params });
    }

    // ── Resource Links ────────────────────────────────────────

    getLinks(projectId: number, owningCompanyId: number): Observable<ProjectResourceLink[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ProjectResourceLink[]>(
            `${this.projectsApiUrl}/${projectId}/links`, { params });
    }

    getLinkCounts(projectId: number, owningCompanyId: number): Observable<Record<string, number>> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<Record<string, number>>(
            `${this.projectsApiUrl}/${projectId}/links/counts`, { params });
    }

    createLink(projectId: number, owningCompanyId: number,
               link: ProjectResourceLink): Observable<ProjectResourceLink> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectResourceLink>(
            `${this.projectsApiUrl}/${projectId}/links`, link, { params });
    }

    deleteLink(projectId: number, linkId: number, owningCompanyId: number,
               reason?: string): Observable<void> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.delete<void>(
            `${this.projectsApiUrl}/${projectId}/links/${linkId}`,
            { params, body: { reason: reason || 'User removed' } });
    }

    findProjectsForResource(resourceType: string, resourceId: number, owningCompanyId: number): Observable<number[]> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('resourceType', resourceType)
            .set('resourceId', resourceId.toString());
        return this.http.get<number[]>(
            `${this.projectsApiUrl}/search/resource-links`,
            { params });
    }

    searchLinkableResources(resourceType: string, query: string, owningCompanyId: number, limit = 20): Observable<LinkableProjectResource[]> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('resourceType', resourceType)
            .set('query', query || '')
            .set('limit', limit.toString());
        return this.http.get<LinkableProjectResource[]>(
            `${this.projectsApiUrl}/search/linkable-resources`,
            { params });
    }

    getTimeline(projectId: number, owningCompanyId: number): Observable<ProjectEvent[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ProjectEvent[]>(`${this.projectsApiUrl}/${projectId}/timeline`, { params });
    }

    getFinancials(projectId: number, owningCompanyId: number, includeFacts = false, scope: 'COMPANY' | 'GROUP' = 'COMPANY'): Observable<ProjectFinancialSummary> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('includeFacts', String(includeFacts))
            .set('scope', scope);
        return this.http.get<ProjectFinancialSummary>(`${this.projectsApiUrl}/${projectId}/financials`, { params });
    }

    recalculateFinancials(projectId: number, owningCompanyId: number): Observable<ProjectFinancialSummary> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectFinancialSummary>(
            `${this.projectsApiUrl}/${projectId}/financials/recalculate`, {}, { params });
    }

    getFinancialFacts(projectId: number, owningCompanyId: number, scope: 'COMPANY' | 'GROUP' = 'COMPANY'): Observable<ProjectFinancialFact[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString()).set('scope', scope);
        return this.http.get<ProjectFinancialFact[]>(
            `${this.projectsApiUrl}/${projectId}/financials/facts`, { params });
    }

    recordProjectFactConversion(projectId: number, owningCompanyId: number, factId: number,
                                rate: number, targetCurrencyCode: string): Observable<ProjectFinancialFact> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.put<ProjectFinancialFact>(
            `${this.projectsApiUrl}/${projectId}/financials/facts/${factId}/conversion`,
            { rate, targetCurrencyCode }, { params });
    }

    getReportsWorkspace(projectId: number, owningCompanyId: number): Observable<ProjectReportWorkspace> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ProjectReportWorkspace>(`${this.projectsApiUrl}/${projectId}/reports`, { params });
    }

    generateExecutionReport(projectId: number, owningCompanyId: number, reportType: string,
                            generatedByCustomerId?: number): Observable<any> {
        let params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('reportType', reportType);
        if (generatedByCustomerId) {
            params = params.set('generatedByCustomerId', generatedByCustomerId.toString());
        }
        return this.http.post<any>(`${this.projectsApiUrl}/${projectId}/reports/execution/generate`, {}, { params });
    }

    generateCompletionReport(projectId: number, owningCompanyId: number,
                             request: ProjectCompletionReportGenerateRequest = {}): Observable<any> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<any>(`${this.projectsApiUrl}/${projectId}/reports/completion/generate`, request, { params });
    }

    exportCompletionReport(projectId: number, reportId: number, owningCompanyId: number,
                           format: 'PDF' | 'PPTX'): Observable<ProjectCompletionReportExport> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('format', format);
        return this.http.post<ProjectCompletionReportExport>(
            `${this.projectsApiUrl}/${projectId}/reports/completion/${reportId}/exports`,
            {},
            { params }
        );
    }

    downloadCompletionReportPdf(projectId: number, reportId: number, owningCompanyId: number): Observable<Blob> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get(`${this.projectsApiUrl}/${projectId}/reports/completion/${reportId}/pdf`, {
            params,
            responseType: 'blob'
        });
    }

    downloadCompletionReportPptx(projectId: number, reportId: number, owningCompanyId: number): Observable<Blob> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get(`${this.projectsApiUrl}/${projectId}/reports/completion/${reportId}/pptx`, {
            params,
            responseType: 'blob'
        });
    }

    getTemplates(owningCompanyId: number, projectType?: string): Observable<ProjectTemplate[]> {
        let params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        if (projectType) {
            params = params.set('projectType', projectType);
        }
        return this.http.get<ProjectTemplate[]>(`${this.projectsApiUrl}/templates`, { params });
    }

    getTemplate(templateCode: string, owningCompanyId: number): Observable<ProjectTemplate> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ProjectTemplate>(`${this.projectsApiUrl}/templates/${templateCode}`, { params });
    }

    applyTemplate(projectId: number, owningCompanyId: number, templateCode: string, overwriteProjectDefaults = true): Observable<LogisticsProject> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<LogisticsProject>(
            `${this.projectsApiUrl}/${projectId}/apply-template`,
            { templateCode, overwriteProjectDefaults },
            { params });
    }

    getMilestones(projectId: number, owningCompanyId: number): Observable<ProjectMilestone[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ProjectMilestone[]>(`${this.projectsApiUrl}/${projectId}/milestones`, { params });
    }

    createMilestone(projectId: number, owningCompanyId: number, milestone: ProjectMilestone): Observable<ProjectMilestone> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectMilestone>(`${this.projectsApiUrl}/${projectId}/milestones`, milestone, { params });
    }

    updateMilestone(projectId: number, owningCompanyId: number, milestoneId: number, request: Partial<ProjectMilestone>): Observable<ProjectMilestone> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.put<ProjectMilestone>(`${this.projectsApiUrl}/${projectId}/milestones/${milestoneId}`, request, { params });
    }

    getPlanningWorkspace(projectId: number, owningCompanyId: number): Observable<ProjectPlanWorkspace> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ProjectPlanWorkspace>(`${this.projectsApiUrl}/${projectId}/planning/workspace`, { params });
    }

    savePlanTask(projectId: number, owningCompanyId: number, task: ProjectPlanTask): Observable<ProjectPlanTask> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        const url = task.planTaskId
            ? `${this.projectsApiUrl}/${projectId}/planning/tasks/${task.planTaskId}`
            : `${this.projectsApiUrl}/${projectId}/planning/tasks`;
        return task.planTaskId
            ? this.http.put<ProjectPlanTask>(url, task, { params })
            : this.http.post<ProjectPlanTask>(url, task, { params });
    }

    bulkUpdatePlanTasks(projectId: number, owningCompanyId: number, request: ProjectPlanBulkTaskUpdate): Observable<ProjectPlanTask[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectPlanTask[]>(`${this.projectsApiUrl}/${projectId}/planning/tasks/bulk`, request, { params });
    }

    deletePlanTask(projectId: number, owningCompanyId: number, planTaskId: number): Observable<void> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<void>(`${this.projectsApiUrl}/${projectId}/planning/tasks/${planTaskId}/delete`, {}, { params });
    }

    indentPlanTask(projectId: number, owningCompanyId: number, planTaskId: number): Observable<ProjectPlanTask> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectPlanTask>(`${this.projectsApiUrl}/${projectId}/planning/tasks/${planTaskId}/indent`, {}, { params });
    }

    outdentPlanTask(projectId: number, owningCompanyId: number, planTaskId: number): Observable<ProjectPlanTask> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectPlanTask>(`${this.projectsApiUrl}/${projectId}/planning/tasks/${planTaskId}/outdent`, {}, { params });
    }

    movePlanTask(projectId: number, owningCompanyId: number, planTaskId: number, sortOrder: number): Observable<ProjectPlanTask> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('sortOrder', sortOrder.toString());
        return this.http.post<ProjectPlanTask>(`${this.projectsApiUrl}/${projectId}/planning/tasks/${planTaskId}/move`, {}, { params });
    }

    completePlanTask(projectId: number, owningCompanyId: number, planTaskId: number): Observable<ProjectPlanTask> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectPlanTask>(`${this.projectsApiUrl}/${projectId}/planning/tasks/${planTaskId}/complete`, {}, { params });
    }

    reopenPlanTask(projectId: number, owningCompanyId: number, planTaskId: number): Observable<ProjectPlanTask> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectPlanTask>(`${this.projectsApiUrl}/${projectId}/planning/tasks/${planTaskId}/reopen`, {}, { params });
    }

    savePlanDependency(projectId: number, owningCompanyId: number, dependency: ProjectPlanDependency): Observable<ProjectPlanDependency> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectPlanDependency>(`${this.projectsApiUrl}/${projectId}/planning/dependencies`, dependency, { params });
    }

    deletePlanDependency(projectId: number, owningCompanyId: number, dependencyId: number): Observable<void> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<void>(`${this.projectsApiUrl}/${projectId}/planning/dependencies/${dependencyId}/delete`, {}, { params });
    }

    savePlanAssignment(projectId: number, owningCompanyId: number, assignment: ProjectPlanAssignment): Observable<ProjectPlanAssignment> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectPlanAssignment>(`${this.projectsApiUrl}/${projectId}/planning/assignments`, assignment, { params });
    }

    deletePlanAssignment(projectId: number, owningCompanyId: number, assignmentId: number): Observable<void> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<void>(`${this.projectsApiUrl}/${projectId}/planning/assignments/${assignmentId}/delete`, {}, { params });
    }

    createPlanBaseline(projectId: number, owningCompanyId: number, request: ProjectPlanBaselineRequest): Observable<ProjectPlanBaseline> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectPlanBaseline>(`${this.projectsApiUrl}/${projectId}/planning/baselines`, request, { params });
    }

    recalculatePlan(projectId: number, owningCompanyId: number): Observable<ProjectPlanWorkspace> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectPlanWorkspace>(`${this.projectsApiUrl}/${projectId}/planning/recalculate`, {}, { params });
    }

    getWorkPackages(projectId: number, owningCompanyId: number): Observable<ProjectWorkPackage[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ProjectWorkPackage[]>(`${this.projectsApiUrl}/${projectId}/work-packages`, { params });
    }

    createWorkPackage(projectId: number, owningCompanyId: number, workPackage: ProjectWorkPackage): Observable<ProjectWorkPackage> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectWorkPackage>(`${this.projectsApiUrl}/${projectId}/work-packages`, workPackage, { params });
    }

    recalculateProgress(projectId: number, owningCompanyId: number): Observable<void> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<void>(`${this.projectsApiUrl}/${projectId}/progress/recalculate`, {}, { params });
    }

    getRequirements(projectId: number, owningCompanyId: number): Observable<ProjectRequirementsSummary> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ProjectRequirementsSummary>(`${this.projectsApiUrl}/${projectId}/requirements`, { params });
    }

    createRequirement(projectId: number, owningCompanyId: number, requirement: ProjectRequirementLine): Observable<ProjectRequirementLine> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectRequirementLine>(`${this.projectsApiUrl}/${projectId}/requirements`, requirement, { params });
    }

    updateRequirement(projectId: number, owningCompanyId: number, requirementId: number, requirement: ProjectRequirementLine): Observable<ProjectRequirementLine> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.put<ProjectRequirementLine>(`${this.projectsApiUrl}/${projectId}/requirements/${requirementId}`, requirement, { params });
    }

    cancelRequirement(projectId: number, owningCompanyId: number, requirementId: number): Observable<ProjectRequirementLine> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectRequirementLine>(`${this.projectsApiUrl}/${projectId}/requirements/${requirementId}/cancel`, {}, { params });
    }

    createRequirementAllocation(projectId: number, owningCompanyId: number, requirementId: number,
                                allocation: ProjectRequirementAllocation): Observable<ProjectRequirementAllocation> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectRequirementAllocation>(
            `${this.projectsApiUrl}/${projectId}/requirements/${requirementId}/allocations`, allocation, { params });
    }

    updateRequirementAllocation(projectId: number, owningCompanyId: number, requirementId: number,
                                allocationId: number, allocation: ProjectRequirementAllocation): Observable<ProjectRequirementAllocation> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.put<ProjectRequirementAllocation>(
            `${this.projectsApiUrl}/${projectId}/requirements/${requirementId}/allocations/${allocationId}`, allocation, { params });
    }

    releaseRequirementAllocation(projectId: number, owningCompanyId: number, requirementId: number,
                                 allocationId: number): Observable<ProjectRequirementAllocation> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectRequirementAllocation>(
            `${this.projectsApiUrl}/${projectId}/requirements/${requirementId}/allocations/${allocationId}/release`, {}, { params });
    }

    linkRequirementFulfillment(projectId: number, owningCompanyId: number, requirementId: number,
                               link: ProjectRequirementFulfillmentLink): Observable<ProjectRequirementFulfillmentLink> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectRequirementFulfillmentLink>(
            `${this.projectsApiUrl}/${projectId}/requirements/${requirementId}/fulfillments`, link, { params });
    }

    updateRequirementFulfillment(projectId: number, owningCompanyId: number, requirementId: number,
                                 fulfillmentLinkId: number,
                                 link: ProjectRequirementFulfillmentLink): Observable<ProjectRequirementFulfillmentLink> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.put<ProjectRequirementFulfillmentLink>(
            `${this.projectsApiUrl}/${projectId}/requirements/${requirementId}/fulfillments/${fulfillmentLinkId}`, link, { params });
    }

    unlinkRequirementFulfillment(projectId: number, owningCompanyId: number, requirementId: number,
                                 fulfillmentLinkId: number): Observable<ProjectRequirementFulfillmentLink> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.delete<ProjectRequirementFulfillmentLink>(
            `${this.projectsApiUrl}/${projectId}/requirements/${requirementId}/fulfillments/${fulfillmentLinkId}`, { params });
    }

    generateRequirementExecution(projectId: number, owningCompanyId: number, requirementId: number): Observable<ProjectRequirementGenerationResponse> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectRequirementGenerationResponse>(
            `${this.projectsApiUrl}/${projectId}/requirements/${requirementId}/generate-execution`, {}, { params });
    }

    recalculateRequirementProgress(projectId: number, owningCompanyId: number): Observable<ProjectRequirementsSummary> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectRequirementsSummary>(`${this.projectsApiUrl}/${projectId}/requirements/recalculate-progress`, {}, { params });
    }

    getProjectCalendar(projectId: number, owningCompanyId: number,
                       filters: { from?: Date; to?: Date; resourceType?: string; status?: string } = {}): Observable<ProjectCalendarEvent[]> {
        let params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        if (filters.from) params = params.set('from', new Date(filters.from).toISOString());
        if (filters.to) params = params.set('to', new Date(filters.to).toISOString());
        if (filters.resourceType) params = params.set('resourceType', filters.resourceType);
        if (filters.status) params = params.set('status', filters.status);
        return this.http.get<ProjectCalendarEvent[]>(`${this.projectsApiUrl}/${projectId}/calendar`, { params });
    }

    getProjectParticipants(projectId: number, owningCompanyId: number): Observable<ProjectParticipant[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ProjectParticipant[]>(`${this.projectsApiUrl}/${projectId}/participants`, { params });
    }

    saveProjectParticipant(projectId: number, owningCompanyId: number, participant: ProjectParticipant): Observable<ProjectParticipant> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        const url = participant.participantId
            ? `${this.projectsApiUrl}/${projectId}/participants/${participant.participantId}`
            : `${this.projectsApiUrl}/${projectId}/participants`;
        return participant.participantId
            ? this.http.put<ProjectParticipant>(url, participant, { params })
            : this.http.post<ProjectParticipant>(url, participant, { params });
    }

    revokeProjectParticipant(projectId: number, owningCompanyId: number, participantId: number): Observable<void> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.delete<void>(`${this.projectsApiUrl}/${projectId}/participants/${participantId}`, { params });
    }

    getProjectDocumentRequirements(projectId: number, owningCompanyId: number): Observable<ProjectDocumentRequirement[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ProjectDocumentRequirement[]>(`${this.projectsApiUrl}/${projectId}/document-requirements`, { params });
    }

    getProjectDocuments(projectId: number, owningCompanyId: number, customerVisibleOnly = false): Observable<ProjectDocument[]> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('customerVisibleOnly', String(customerVisibleOnly));
        return this.http.get<ProjectDocument[]>(`${this.projectsApiUrl}/${projectId}/documents`, { params });
    }

    applyProjectDocumentRequirements(projectId: number, owningCompanyId: number): Observable<ProjectDocument[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectDocument[]>(`${this.projectsApiUrl}/${projectId}/documents/apply-requirements`, {}, { params });
    }

    saveProjectDocument(projectId: number, owningCompanyId: number, document: ProjectDocument): Observable<ProjectDocument> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        const url = document.documentId
            ? `${this.projectsApiUrl}/${projectId}/documents/${document.documentId}`
            : `${this.projectsApiUrl}/${projectId}/documents`;
        return document.documentId
            ? this.http.put<ProjectDocument>(url, document, { params })
            : this.http.post<ProjectDocument>(url, document, { params });
    }

    getProjectComments(projectId: number, owningCompanyId: number, customerVisibleOnly = false): Observable<ProjectComment[]> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('customerVisibleOnly', String(customerVisibleOnly));
        return this.http.get<ProjectComment[]>(`${this.projectsApiUrl}/${projectId}/comments`, { params });
    }

    addProjectComment(projectId: number, owningCompanyId: number, comment: ProjectComment): Observable<ProjectComment> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectComment>(`${this.projectsApiUrl}/${projectId}/comments`, comment, { params });
    }

    publishProjectCustomerUpdate(projectId: number, owningCompanyId: number, update: ProjectComment): Observable<ProjectComment> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectComment>(`${this.projectsApiUrl}/${projectId}/customer-updates`, update, { params });
    }

    deleteProjectComment(projectId: number, owningCompanyId: number, commentId: number): Observable<void> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.delete<void>(`${this.projectsApiUrl}/${projectId}/comments/${commentId}`, { params });
    }

    getProjectCustomerView(projectId: number, owningCompanyId: number): Observable<ProjectCustomerView> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ProjectCustomerView>(`${this.projectsApiUrl}/${projectId}/customer-view`, { params });
    }

    getProjectRisks(projectId: number, owningCompanyId: number, activeOnly = false): Observable<ProjectRiskIssue[]> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('activeOnly', String(activeOnly));
        return this.http.get<ProjectRiskIssue[]>(`${this.projectsApiUrl}/${projectId}/risks`, { params });
    }

    refreshProjectRisks(projectId: number, owningCompanyId: number): Observable<ProjectRiskIssue[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectRiskIssue[]>(`${this.projectsApiUrl}/${projectId}/risks/refresh`, {}, { params });
    }

    saveProjectRisk(projectId: number, owningCompanyId: number, risk: ProjectRiskIssue): Observable<ProjectRiskIssue> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        const url = risk.riskIssueId
            ? `${this.projectsApiUrl}/${projectId}/risks/${risk.riskIssueId}`
            : `${this.projectsApiUrl}/${projectId}/risks`;
        return risk.riskIssueId
            ? this.http.put<ProjectRiskIssue>(url, risk, { params })
            : this.http.post<ProjectRiskIssue>(url, risk, { params });
    }

    updateProjectRiskStatus(projectId: number, owningCompanyId: number, riskIssueId: number,
                            request: { status?: string; resolutionComment?: string; overrideReason?: string }): Observable<ProjectRiskIssue> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectRiskIssue>(`${this.projectsApiUrl}/${projectId}/risks/${riskIssueId}/status`, request, { params });
    }

    getProjectClosureReadiness(projectId: number, owningCompanyId: number): Observable<ProjectClosureReview> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ProjectClosureReview>(`${this.projectsApiUrl}/${projectId}/closure/readiness`, { params });
    }

    approveProjectClosure(projectId: number, owningCompanyId: number,
                          request: { override?: boolean; overrideReason?: string; closureReason?: string; closureComment?: string }): Observable<ProjectClosureReview> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.post<ProjectClosureReview>(`${this.projectsApiUrl}/${projectId}/closure/approve`, request, { params });
    }

    getProjectClosureReviews(projectId: number, owningCompanyId: number): Observable<ProjectClosureReview[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ProjectClosureReview[]>(`${this.projectsApiUrl}/${projectId}/closure/reviews`, { params });
    }

    getProjectChangeRequests(projectId: number, owningCompanyId: number): Observable<ProjectChangeRequest[]> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        return this.http.get<ProjectChangeRequest[]>(`${this.projectsApiUrl}/${projectId}/change-requests`, { params });
    }

    saveProjectChangeRequest(projectId: number, owningCompanyId: number,
                             request: ProjectChangeRequest): Observable<ProjectChangeRequest> {
        const params = new HttpParams().set('owningCompanyId', owningCompanyId.toString());
        const url = request.changeRequestId
            ? `${this.projectsApiUrl}/${projectId}/change-requests/${request.changeRequestId}`
            : `${this.projectsApiUrl}/${projectId}/change-requests`;
        return request.changeRequestId
            ? this.http.put<ProjectChangeRequest>(url, request, { params })
            : this.http.post<ProjectChangeRequest>(url, request, { params });
    }
}
